import React, { useState } from 'react';

import { LayoutItem } from '@blockworks/platform/api/research/studio';
import { Button, FlexBox, Modal, ModalProps, TrashIcon } from '@blockworks/ui';

import { MarkdownEditorWithPreview } from '../../../markdown';
import { useDashboardGridContext } from '../../context';
import { DRAGGABLE_CANCEL_CLASS } from '../../dashboard-grid.model';
import { removeLayoutItem, updateLayoutItem } from '../../utils';

type MarkdownEditorModalProps = { item: LayoutItem; onSave?: () => void; close: () => void } & Pick<
    ModalProps,
    'open' | 'onOpenChange'
>;

const resetValue = (setValue: (value: string) => void, value?: string) => {
    setValue(value ?? '');
};

const MarkdownEditorModal: React.FC<MarkdownEditorModalProps> = ({ item, open, onOpenChange, close }) => {
    const [value, setValue] = useState(item.data?.value ?? '');
    const { layouts, setLayouts } = useDashboardGridContext();

    const onSave = () => {
        if (!layouts) return;
        setLayouts(updateLayoutItem(layouts, item.i, { value }));
        close();
    };

    const onRemove = () => {
        if (!layouts) return;
        setLayouts(removeLayoutItem(layouts, item.i));
        close();
    };

    const handleOnOpenChange = (open: boolean) => {
        if (!open) {
            resetValue(setValue, item.data?.value);
        }
        onOpenChange?.(open);
    };

    const handleOnClose = () => {
        resetValue(setValue, item.data?.value);
        close();
    };

    return (
        <Modal
            classNames={{ base: `h-[85vh] w-[85vw] md:h-[75vh] md:w-[75vw] ${DRAGGABLE_CANCEL_CLASS}` }}
            open={open}
            onOpenChange={handleOnOpenChange}
        >
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>Edit Text</Modal.Title>
                </Modal.Header>
                <Modal.Body className="!flex-grow-0 items-center">
                    {item && (
                        <MarkdownEditorWithPreview
                            markdown={value}
                            onChange={setValue}
                            placeholder="## Use markdown syntax to compose content"
                            className="h-[calc(100%-20px)]"
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        size="2sm"
                        borderRadius="md"
                        leadingIcon={TrashIcon}
                        variant="ghost"
                        intent="danger"
                        onClick={onRemove}
                    >
                        Remove
                    </Button>
                    <FlexBox gap={2} justifyContent="end" w="full">
                        <Button size="2sm" borderRadius="md" onClick={handleOnClose}>
                            Cancel
                        </Button>
                        <Button intent="primary" size="2sm" borderRadius="md" onClick={onSave}>
                            Save
                        </Button>
                    </FlexBox>
                </Modal.Footer>
            </Modal.Content>
        </Modal>
    );
};

export type { MarkdownEditorModalProps };
export { MarkdownEditorModal };
